import { Injectable, inject } from '@angular/core';
import { Firestore, collection, query, where, getDocs, doc, writeBatch, deleteDoc, DocumentData, QuerySnapshot, getDoc } from '@angular/fire/firestore';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';

@Injectable({
  providedIn: 'root'
})
export class ProfitReportService {
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private historyCollection = 'profit_report_upload_history';
  private dataCollection = 'profit_report_data';

  createId(): string {
    return doc(collection(this.firestore, 'id')).id;
  }

  async getHistoryByPeriod(period: string): Promise<QuerySnapshot<DocumentData>> {
    const historyCol = collection(this.firestore, this.historyCollection);
    const q = query(historyCol, where('period', '==', period));
    return getDocs(q);
  }

  async getDuplicateHistory(period: string, fileName: string) {
    const snapshot = await this.getHistoryByPeriod(period);
    return snapshot.docs.find(d => {
      const data = d.data();
      const dbFileName = (data['fileName'] || '').normalize('NFC').trim().toLowerCase();
      const uploadFileName = fileName.normalize('NFC').trim().toLowerCase();
      return dbFileName === uploadFileName;
    });
  }

  async deleteImportData(importId: string) {
    const dataCol = collection(this.firestore, this.dataCollection);
    const q = query(dataCol, where('importId', '==', importId));
    const snapshot = await getDocs(q);
    const deleteBatch = writeBatch(this.firestore);
    snapshot.docs.forEach(d => deleteBatch.delete(d.ref));
    await deleteBatch.commit();
  }

  async uploadRawImportFile(file: File, period: string): Promise<string> {
    const sanitizedPeriod = period.replace('/', '-');
    const filePath = `Platform/ProfitReports/RawImports/${sanitizedPeriod}/${Date.now()}_${file.name}`;
    const storageRef = ref(this.storage, filePath);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  }

  async deleteHistory(historyId: string) {
    try {
      const docRef = doc(this.firestore, this.historyCollection, historyId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const downloadURL = snap.data()['downloadURL'];
        if (downloadURL) {
          const match = downloadURL.match(/o\/(.+?)\?alt=media/);
          if (match && match[1]) {
            const storagePath = decodeURIComponent(match[1]);
            const storageRef = ref(this.storage, storagePath);
            await deleteObject(storageRef).catch(() => {});
          }
        }
      }
    } catch (e) {
      console.warn('Error cleaning up storage for history', e);
    }
    await deleteDoc(doc(this.firestore, this.historyCollection, historyId));
  }

  async saveProfitReportDataBatch(rows: any[], importId: string, period: string, isImportingCheck: () => boolean) {
    const dataCol = collection(this.firestore, this.dataCollection);
    const batchSize = 490;
    
    for (let i = 0; i < rows.length; i += batchSize) {
      if (!isImportingCheck()) {
        throw new Error('Import cancelled by user');
      }

      const dataBatch = writeBatch(this.firestore);
      const chunk = rows.slice(i, i + batchSize);
      
      chunk.forEach(row => {
        const rowRef = doc(dataCol);
        dataBatch.set(rowRef, {
          importId,
          period: period,
          postingDate: row['Posting Date'] || null,
          company: row['Company'] || '',
          companyId: row['companyId'] || null,
          checkNumber: row['Check or Slip #'] || '',
          description: row['Description'] || '',
          category: row['Categories'] || '', 
          amount: typeof row['Amount'] === 'number' ? row['Amount'] : parseFloat(row['Amount'] || '0')
        });
      });
      await dataBatch.commit();
    }
  }

  async saveUploadHistory(historyData: any) {
    const historyRef = doc(collection(this.firestore, this.historyCollection));
    const historyBatch = writeBatch(this.firestore);
    historyBatch.set(historyRef, historyData);
    await historyBatch.commit();
  }

  async deleteSingleFileHistoryAndData(historyId: string, importId: string, downloadURL?: string) {
    // 1. Delete data rows with this importId
    if (importId) {
      await this.deleteImportData(importId);
    }

    // 2. Delete file from Storage and history doc
    if (historyId) {
      await this.deleteHistory(historyId);
    }
  }

  async deleteHistoryAndDataByPeriod(period: string) {
    const snapshot = await this.getHistoryByPeriod(period);
    if (snapshot.empty) return;
    
    const batch = writeBatch(this.firestore);
    
    for (const historyDoc of snapshot.docs) {
      const data = historyDoc.data();
      const importId = data['importId'];
      const downloadURL = data['downloadURL'];

      if (downloadURL) {
        try {
          const match = downloadURL.match(/o\/(.+?)\?alt=media/);
          if (match && match[1]) {
            const storagePath = decodeURIComponent(match[1]);
            const storageRef = ref(this.storage, storagePath);
            await deleteObject(storageRef).catch(() => {});
          }
        } catch (e) {}
      }
      
      const dataCol = collection(this.firestore, this.dataCollection);
      const dataQ = query(dataCol, where('importId', '==', importId));
      const dataSnapshot = await getDocs(dataQ);
      dataSnapshot.docs.forEach(d => batch.delete(d.ref));
      
      batch.delete(historyDoc.ref);
    }
    
    await batch.commit();
  }

  async getProfitDataByPeriod(period: string): Promise<any[]> {
    const profitRef = collection(this.firestore, this.dataCollection);
    const qProfit = query(profitRef, where('period', '==', period));
    const profitSnap = await getDocs(qProfit);
    return profitSnap.docs.map(d => d.data());
  }

  async getProfitDataByCompany(period: string, companyId: string): Promise<any[]> {
    const profitRef = collection(this.firestore, this.dataCollection);
    const qProfit = query(profitRef, 
      where('period', '==', period),
      where('companyId', '==', companyId)
    );
    const profitSnap = await getDocs(qProfit);
    return profitSnap.docs.map(d => d.data());
  }

  async getProfitDataByYear(year: string): Promise<any[]> {
    const profitRef = collection(this.firestore, this.dataCollection);
    const prefix = `${year}/`;
    const qProfit = query(profitRef, 
      where('period', '>=', prefix),
      where('period', '<=', prefix + '\uf8ff')
    );
    const profitSnap = await getDocs(qProfit);
    return profitSnap.docs.map(d => d.data());
  }

  async calculatePartnerDistribution(companyId: string, netIncome: number, period: string, ownerId?: string): Promise<any[]> {
    const ownershipsRef = collection(this.firestore, 'company_ownerships');
    let q = query(ownershipsRef, where('companyId', '==', companyId));
    
    if (ownerId) {
      q = query(q, where('ownerId', '==', ownerId));
    }

    const ownershipsSnap = await getDocs(q);
    
    const partners = await Promise.all(ownershipsSnap.docs.map(async (d) => {
      const data = d.data();
      const oid = data['ownerId'];
      const percentage = Number(data['profitSharePercent'] || 0);
      
      const ownerDocRef = doc(this.firestore, 'owners', oid);
      const ownerSnap = await getDoc(ownerDocRef);
      const ownerName = ownerSnap.exists() ? ownerSnap.data()['name'] : 'Unknown Owner';


      
      return {
        name: ownerName,
        percentage: percentage,
        amount: netIncome * (percentage / 100)
      };
    }));

    return partners;
  }

  async calculatePartnerDistributionByYear(companyId: string, netIncome: number, year: string, ownerId?: string): Promise<any[]> {
    return this.calculatePartnerDistribution(companyId, netIncome, year, ownerId);
  }

  async savePeriodSummaries(summaries: any[], period: string) {
    const batch = writeBatch(this.firestore);
    const collectionRef = collection(this.firestore, 'period_summaries');

    summaries.forEach(summary => {
      const docId = `${period.replace('/', '-')}-${summary.companyId}`;
      const docRef = doc(collectionRef, docId);
      batch.set(docRef, {
        ...summary,
        updatedAt: new Date()
      });
    });

    await batch.commit();
  }

  async getPeriodSummary(period: string, companyId: string): Promise<any | null> {
    const docId = `${period.replace('/', '-')}-${companyId}`;
    const docRef = doc(this.firestore, 'period_summaries', docId);
    const snap = await getDoc(docRef);
    return snap.exists() ? snap.data() : null;
  }

  /**
   * Calculates total operating expenses per company from expense data.
   * @param expenseData Array of expense report data items.
   * @param validCategories Set of lowercase category names to include.
   * @param period Optional period string (YYYY/MM) to filter by posting date.
   * @returns Map of companyId to total expense amount.
   */
  calculateOperatingExpenses(expenseData: any[], validCategories: Set<string>, period?: string): Map<string, number> {
    const companyExpenses = new Map<string, number>();

    expenseData.forEach(d => {
      if (period && d['postingDate']) {
        const pDate = d['postingDate'].toDate ? d['postingDate'].toDate() : new Date(d['postingDate']);
        if (!isNaN(pDate.getTime())) {
          const year = pDate.getFullYear();
          const month = (pDate.getMonth() + 1).toString().padStart(2, '0');
          if (`${year}/${month}` !== period) return;
        }
      }

      const cid = d['companyId'];
      const cat = (d['category'] || '').trim().toLowerCase();
      
      if (cid && validCategories.has(cat)) {
        const amt = Number(d['amount'] || 0);
        companyExpenses.set(cid, (companyExpenses.get(cid) || 0) + amt);
      }
    });

    return companyExpenses;
  }

  /**
   * Calculates total operating expenses and fees (Expenses - Fees).
   * @param companyExpenses Map of companyId to total operating expenses.
   * @param companyFees Map of companyId to total fees.
   * @returns Map of companyId to total (Expenses - Fees).
   */
  calculateTotalOpsAndFees(companyExpenses: Map<string, number>, companyFees: Map<string, number>): Map<string, number> {
    const result = new Map<string, number>();
    const allCids = new Set([...companyExpenses.keys(), ...companyFees.keys()]);
    
    allCids.forEach(cid => {
      const exp = companyExpenses.get(cid) || 0;
      const fee = companyFees.get(cid) || 0;
      result.set(cid, exp - fee);
    });
    
    return result;
  }

  async uploadTempPdf(userId: string, fileNameRef: string, pdfBlob: Blob): Promise<{ downloadURL: string, storagePath: string }> {
    const filename = `users/${userId || 'temp'}/temp_profit_reports/${fileNameRef}_${Date.now()}.pdf`;
    const storageRef = ref(this.storage, filename);
    await uploadBytes(storageRef, pdfBlob, { contentType: 'application/pdf' });
    const downloadURL = await getDownloadURL(storageRef);
    return { downloadURL, storagePath: filename };
  }

  async deleteTempPdf(storagePath: string) {
    await deleteObject(ref(this.storage, storagePath));
  }
}